import { type HTMLAttributes, useMemo } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/cn';

/* --------------------------------------------------------------------------
   Palette for initials avatars — warm, earthy tones matching brand identity
   -------------------------------------------------------------------------- */

const AVATAR_COLORS = [
  { bg: '#C4714A', text: '#FFFFFF' }, // Terracotta
  { bg: '#8B9E78', text: '#FFFFFF' }, // Sage
  { bg: '#4A7B9D', text: '#FFFFFF' }, // Muted blue
  { bg: '#9B7C5E', text: '#FFFFFF' }, // Warm tan
  { bg: '#7B6B8A', text: '#FFFFFF' }, // Dusty lavender
  { bg: '#5A8A4E', text: '#FFFFFF' }, // Forest green
  { bg: '#A0522D', text: '#FFFFFF' }, // Brick
  { bg: '#6B8E8E', text: '#FFFFFF' }, // Teal sage
] as const;

function hashName(name: string): number {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash);
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

/* --------------------------------------------------------------------------
   Variants
   -------------------------------------------------------------------------- */

const avatarVariants = cva(
  [
    'inline-flex items-center justify-center',
    'rounded-full font-[family-name:var(--font-heading)] font-bold',
    'select-none shrink-0',
    'overflow-hidden',
  ].join(' '),
  {
    variants: {
      size: {
        sm: 'h-8 w-8 text-xs',
        md: 'h-10 w-10 text-sm',
        lg: 'h-14 w-14 text-lg',
        xl: 'h-20 w-20 text-2xl',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  },
);

/* --------------------------------------------------------------------------
   Types
   -------------------------------------------------------------------------- */

export interface AvatarProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof avatarVariants> {
  /** Full name — used for initials & color derivation. */
  name: string;
  /** Optional image URL. If provided, renders image instead of initials. */
  src?: string | null;
}

/* --------------------------------------------------------------------------
   Component
   -------------------------------------------------------------------------- */

export function Avatar({
  className,
  size,
  name,
  src,
  ...props
}: AvatarProps) {
  const initials = useMemo(() => getInitials(name), [name]);
  const color = useMemo(
    () => AVATAR_COLORS[hashName(name) % AVATAR_COLORS.length],
    [name],
  );

  return (
    <div
      className={cn(avatarVariants({ size }), className)}
      style={src ? undefined : { backgroundColor: color.bg, color: color.text }}
      role="img"
      aria-label={name}
      {...props}
    >
      {src ? (
        <img
          src={src}
          alt={name}
          className="h-full w-full object-cover"
          loading="lazy"
        />
      ) : (
        <span aria-hidden>{initials}</span>
      )}
    </div>
  );
}

export { avatarVariants };
